import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { rtdb } from "../../config/firebase"; // Pastikan path ini benar
import { ref as dbRef, push, update } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  XMarkIcon, CheckCircleIcon, UserIcon, IdentificationIcon, MapPinIcon, DevicePhoneMobileIcon, AcademicCapIcon,
  CalendarIcon, BriefcaseIcon, ShieldCheckIcon, ClipboardDocumentCheckIcon, GlobeAsiaAustraliaIcon,
  DocumentTextIcon, PhotoIcon, BuildingOfficeIcon, ArrowUpOnSquareIcon, PaperClipIcon, EnvelopeIcon
} from "@heroicons/react/24/solid";

// =======================================
// 🔹 KONFIGURASI FORM TERPUSAT
// =======================================
const FORM_CONFIG = {
  // Key: nama field, Value: properti
  nama: { label: "Nama Lengkap", Icon: UserIcon, required: true },
  nik: { label: "NIK (16 digit)", Icon: IdentificationIcon, required: true },
  npwp: { label: "Nomor NPWP", Icon: DocumentTextIcon },
  tempat: { label: "Tempat Lahir", Icon: MapPinIcon, required: true },
  tanggalLahir: { label: "Tanggal Lahir", Icon: CalendarIcon, type: "date", required: true },
  jenisKelamin: { label: "Jenis Kelamin", Icon: UserIcon, type: "select", options: ["Laki-laki", "Perempuan"], required: true },
  agama: { label: "Agama", Icon: GlobeAsiaAustraliaIcon, type: "select", options: ["Islam", "Kristen", "Katolik", "Hindu", "Buddha", "Konghucu"] },
  pendidikan: { label: "Pendidikan Terakhir", Icon: AcademicCapIcon },
  fotoIjazah: { label: "Foto Ijazah Terakhir", Icon: PhotoIcon, type: "file" },
  pasFoto: { label: "Pas Foto", Icon: PhotoIcon, type: "file" },

  // Pekerjaan
  namaPerusahaan: { label: "Nama Perusahaan", Icon: BuildingOfficeIcon },
  jabatan: { label: "Jabatan", Icon: BriefcaseIcon },
  // nip removed
  tanggalMasuk: { label: "Tanggal Masuk Kerja", Icon: CalendarIcon, type: "date" },
  dplk: { label: "Nomor DPLK", Icon: ClipboardDocumentCheckIcon },
  bpjsKet: { label: "Nomor BPJS Ketenagakerjaan", Icon: ShieldCheckIcon },
  bpjsKes: { label: "Nomor BPJS Kesehatan", Icon: ShieldCheckIcon },

  // Kontak / Alamat (baru)
  alamat: { label: "Alamat Lengkap", Icon: MapPinIcon, type: "textarea", colSpan: 2 },
  email: { label: "Email Pribadi", Icon: EnvelopeIcon, required: true },

  // Lainnya
  noHp: { label: "Nomor Handphone", Icon: DevicePhoneMobileIcon, required: true },
  ket: { label: "Keterangan Tambahan", type: "textarea", colSpan: 2 },
};

const STEPS_CONFIG = [
  { 
    id: 1, 
    name: "Data Pribadi", 
    fields: [
      "nama", 
      "nik", 
      "npwp", 
      "tempat", 
      "tanggalLahir", 
      "jenisKelamin", 
      "agama", 
      "pendidikan", 
      "fotoIjazah", 
      "pasFoto"
    ] 
  },
  // Data Kerja (NIP sudah dihapus dari sini)
  { 
    id: 2, 
    name: "Data Kerja", 
    fields: [
      "namaPerusahaan", 
      "jabatan", 
      "tanggalMasuk", 
      "dplk", 
      "bpjsKet", 
      "bpjsKes"
    ] 
  },
  // Sekarang berisi alamat + email + noHp + ket
  { 
    id: 3, 
    name: "Domisili & Kontak", 
    fields: [
      "alamat", 
      "email", 
      "noHp", 
      "ket"
    ] 
  },
  { id: 4, name: "Konfirmasi", fields: [] },
];


// =======================================
// 🔹 CUSTOM HOOK: Logika Manajemen Form
// =======================================
const useDataCleaningForm = ({ initialData, onSave, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialData || {});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const storage = getStorage();

  const completion = useMemo(() => {
    const definedFields = Object.keys(FORM_CONFIG);
    const totalFields = definedFields.length;
    if (totalFields === 0) return 0;

    const filledCount = definedFields.reduce((acc, field) => {
      const value = form[field];
      if (value !== '' && value != null && value !== undefined) {
        return acc + 1;
      }
      return acc;
    }, 0);

    return Math.round((filledCount / totalFields) * 100);
  }, [form]);

  const validateStep = (stepIndex) => {
    const currentStepFields = STEPS_CONFIG[stepIndex - 1].fields;
    const newErrors = {};
    currentStepFields.forEach(field => {
      const config = FORM_CONFIG[field];
      if (config.required && (!form[field] || String(form[field]).trim() === '')) {
        newErrors[field] = `${config.label} wajib diisi.`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(p => Math.min(p + 1, STEPS_CONFIG.length));
    }
  };

  const handleBack = () => setCurrentStep(p => Math.max(p - 1, 1));
  
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  }, [errors]);

  const handleFileChange = useCallback((e, fieldName) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm(prev => ({ ...prev, [fieldName]: file }));
      if (errors[fieldName]) setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  }, [errors]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const uploadedFileUrls = {};
      const fileFields = Object.keys(FORM_CONFIG).filter(key => FORM_CONFIG[key].type === 'file');

      for (const field of fileFields) {
        const file = form[field];
        if (file instanceof File) {
          const path = `DataCleaning/${field}/${Date.now()}_${file.name}`;
          const fileRef = storageRef(storage, path);
          await uploadBytes(fileRef, file);
          uploadedFileUrls[field] = await getDownloadURL(fileRef);
        }
      }
      
      const finalData = { ...form, ...uploadedFileUrls };
      Object.keys(finalData).forEach(key => finalData[key] === undefined && delete finalData[key]);
      
      if (initialData?.id) {
        await update(dbRef(rtdb, `anggota_cleaning/${initialData.id}`), finalData);
      } else {
        await push(dbRef(rtdb, "anggota_cleaning"), finalData);
      }
      
      onSave(finalData);
      onClose();
    } catch (err) {
      console.error("Gagal menyimpan data:", err);
      alert(err.message || "Terjadi kesalahan saat menyimpan data.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return { form, errors, isSubmitting, currentStep, completion, handlers: { handleChange, handleFileChange, handleSubmit, handleNext, handleBack } };
};

// =======================================
// 🔹 KOMPONEN UI KECIL (UI Components)
// =======================================
const ModalHeader = ({ title, onClose, isSubmitting }) => (
  <header className="flex justify-between items-center p-5 border-b bg-slate-50 rounded-t-xl">
    <h2 className="text-lg font-bold text-slate-800">{title}</h2>
    <button onClick={onClose} disabled={isSubmitting} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-200 disabled:opacity-50">
      <XMarkIcon className="w-5 h-5" />
    </button>
  </header>
);

const ModalStepper = ({ steps, currentStep, completion }) => (
  <div className="p-6 border-b bg-white">
    <div className="flex justify-between items-center mb-2">
      <p className="text-sm font-semibold text-green-700">Progres Pengisian</p>
      <p className="text-sm font-bold text-green-700">{completion}%</p>
    </div>
    <div className="w-full bg-slate-200 rounded-full h-2.5 mb-6">
      <motion.div className="bg-green-600 h-2.5 rounded-full" animate={{ width: `${completion}%` }} />
    </div>
    <nav className="flex items-start justify-between">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center w-full text-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${currentStep >= step.id ? "bg-green-600 text-white" : "bg-slate-200 text-slate-600"}`}>
              {currentStep > step.id ? <CheckCircleIcon className="w-5 h-5" /> : step.id}
            </div>
            <p className={`mt-2 text-xs font-semibold transition-colors duration-300 ${currentStep >= step.id ? "text-green-700" : "text-slate-500"}`}>{step.name}</p>
          </div>
          {index < steps.length - 1 && <div className={`flex-grow h-0.5 mt-4 mx-2 transition-colors duration-300 ${currentStep > step.id ? "bg-green-600" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </nav>
  </div>
);

const FormField = ({ name, formState, error, config, onChange, onFileChange }) => {
  const { label, Icon, type, options } = config;
  const value = formState[name];
  const inputBaseClass = "w-full py-2 text-sm border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-150";

  if (type === "file") {
    const file = formState[name];
    const previewUrl = file instanceof File ? URL.createObjectURL(file) : (typeof file === 'string' ? file : null);
    
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
        <div className="flex items-center gap-4">
          <label htmlFor={name} className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-green-700 bg-green-100 border border-transparent rounded-md hover:bg-green-200 transition">
            <ArrowUpOnSquareIcon className="w-5 h-5" />
            <span>{file ? "Ganti File" : "Pilih File"}</span>
            <input id={name} name={name} type="file" accept="image/*" onChange={(e) => onFileChange(e, name)} className="hidden" />
          </label>
          {file && <span className="text-sm text-slate-500 flex items-center gap-1.5"><PaperClipIcon className="w-4 h-4" />{typeof file === 'string' ? 'File tersimpan' : file.name}</span>}
        </div>
        {previewUrl && <img src={previewUrl} alt={`Preview ${label}`} className="mt-3 w-28 h-28 object-cover rounded-md border border-slate-200" />}
      </div>
    );
  }
  
  const commonProps = { id: name, name, value: value || "", onChange };
  
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        {Icon && type !== "textarea" && <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"><Icon className="w-5 h-5 text-slate-400" /></div>}
        {type === "select" ? (
          <select {...commonProps} className={`${inputBaseClass} pl-10 pr-3 appearance-none`}>
            <option value="">Pilih {label}</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        ) : type === "textarea" ? (
          <textarea {...commonProps} rows="3" className={`${inputBaseClass} px-3`} />
        ) : (
          <input type={type || "text"} {...commonProps} className={`${inputBaseClass} pl-10 pr-3`} />
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

const StepRenderer = ({ step, form, errors, handleChange, handleFileChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
    {step.fields.map((fieldName) => {
      const config = FORM_CONFIG[fieldName];
      return (
        <div key={fieldName} className={config.colSpan === 2 ? "md:col-span-2" : ""}>
          <FormField name={fieldName} formState={form} error={errors[fieldName]} config={config} onChange={handleChange} onFileChange={handleFileChange} />
        </div>
      );
    })}
  </div>
);

const ConfirmationStep = ({ data }) => {
    return (
        <div className="space-y-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-center">
                <CheckCircleIcon className="w-6 h-6 mr-3 text-green-600" />
                <div>
                    <h3 className="font-bold">Periksa Kembali Data Anda</h3>
                    <p className="text-sm">Pastikan semua informasi yang Anda masukkan sudah benar sebelum menyimpan.</p>
                </div>
            </div>
            <div className="border rounded-lg p-6 space-y-4">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {Object.entries(data).map(([key, value]) => {
                        if (!value || !FORM_CONFIG[key]) return null;
                        
                        const isFile = FORM_CONFIG[key].type === 'file';
                        const displayValue = (typeof value === 'object' && value.name) ? value.name : value;

                        return (
                            <div key={key}>
                                <p className="text-xs text-slate-500">{FORM_CONFIG[key].label}</p>
                                {isFile ? (
                                    <div className="flex items-center gap-2 mt-1">
                                        <img src={typeof value === 'string' ? value : URL.createObjectURL(value)} alt="preview" className="w-10 h-10 rounded object-cover"/>
                                        <p className="text-sm font-semibold text-slate-700 truncate">{displayValue}</p>
                                    </div>
                                ) : (
                                    <p className="text-sm font-semibold text-slate-700">{displayValue}</p>
                                )}
                            </div>
                        );
                    })}
                 </div>
            </div>
        </div>
    );
};

const ModalFooter = ({ currentStep, totalSteps, isSubmitting, onBack, onNext }) => (
  <footer className="flex justify-between items-center gap-3 px-6 py-4 border-t bg-slate-50 rounded-b-xl">
    <button type="button" onClick={onBack} disabled={currentStep === 1 || isSubmitting} className="px-5 py-2 text-sm font-semibold bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">
      Kembali
    </button>
    {currentStep < totalSteps ? (
      <button type="button" onClick={onNext} disabled={isSubmitting} className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 transition">
        Lanjut
      </button>
    ) : (
      <button type="submit" disabled={isSubmitting} className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center justify-center min-w-[130px] transition disabled:opacity-50 disabled:bg-green-400">
        {isSubmitting ? "Menyimpan..." : "Simpan Data"}
      </button>
    )}
  </footer>
);


// =======================================
// 🔹 KOMPONEN UTAMA (Main Component)
// =======================================
export default function ModalDataCleaning({ initialData, onClose, onSave }) {
  const { form, errors, isSubmitting, currentStep, completion, handlers } = useDataCleaningForm({ initialData, onSave, onClose });
  const activeStep = STEPS_CONFIG.find((s) => s.id === currentStep);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className="bg-white rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden"
      >
        <ModalHeader
          title={initialData ? "Edit Data Cleaning Service" : "Tambah Data Baru"}
          onClose={onClose}
          isSubmitting={isSubmitting}
        />
        <form onSubmit={handlers.handleSubmit} className="flex flex-col flex-grow overflow-hidden">
          <div className="sticky top-0 bg-white z-10 border-b">
            <ModalStepper steps={STEPS_CONFIG} currentStep={currentStep} completion={completion} />
          </div>
          <main className="flex-grow overflow-y-auto px-6 py-8 scrollbar-thin scrollbar-thumb-slate-300 hover:scrollbar-thumb-slate-400">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -50, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {currentStep === STEPS_CONFIG.length ? (
                  <ConfirmationStep data={form} />
                ) : (
                  <StepRenderer
                    step={activeStep}
                    form={form}
                    errors={errors}
                    handleChange={handlers.handleChange}
                    handleFileChange={handlers.handleFileChange}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </main>
          <ModalFooter
            currentStep={currentStep}
            totalSteps={STEPS_CONFIG.length}
            isSubmitting={isSubmitting}
            onBack={handlers.handleBack}
            onNext={handlers.handleNext}
          />
        </form>
      </motion.div>
    </div>
  );
}